import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { colors } from '../styles';

export interface FileInputHandle {
  reset: () => void;
}

interface FileInputProps {
  accept?: string;
  disabled?: boolean;
  selected?: File | null;
  onChange: (file: File | null) => void;
  placeholder?: string;
  hint?: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileInput = forwardRef<FileInputHandle, FileInputProps>(
  (
    {
      accept = '.png,.jpg,.jpeg,.pdf',
      disabled,
      selected,
      onChange,
      placeholder = 'Drop a file or click to browse',
      hint,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [dragOver, setDragOver] = useState(false);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (inputRef.current) inputRef.current.value = '';
        onChange(null);
      },
    }));

    const openPicker = () => {
      if (disabled) return;
      inputRef.current?.click();
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (inputRef.current) inputRef.current.value = '';
      onChange(null);
    };

    const handleFiles = (files: FileList | null) => {
      const file = files?.[0] || null;
      onChange(file);
    };

    return (
      <div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={(e) => handleFiles(e.target.files)}
          style={{ display: 'none' }}
        />
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker();
            }
          }}
          onClick={openPicker}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (disabled) return;
            handleFiles(e.dataTransfer.files);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '18px 16px',
            borderRadius: 10,
            border: `2px dashed ${dragOver ? colors.accent : selected ? '#86EFAC' : colors.border}`,
            background: selected ? '#F0FDF4' : dragOver ? colors.accentSoft : '#F9FAFB',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            transition: 'background 0.15s ease, border-color 0.15s ease',
          }}
        >
          {selected ? (
            <>
              <i className="fas fa-file-alt" style={{ fontSize: 22, color: colors.success }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: colors.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selected.name}
                </div>
                <div style={{ fontSize: 12, color: colors.muted }}>{formatBytes(selected.size)}</div>
              </div>
              <button
                type="button"
                onClick={handleClear}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: colors.muted,
                  cursor: 'pointer',
                  fontSize: 14,
                  padding: 4,
                }}
                aria-label="Remove file"
              >
                <i className="fas fa-times" />
              </button>
            </>
          ) : (
            <>
              <i className="fas fa-cloud-upload-alt" style={{ fontSize: 22, color: colors.accent }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{placeholder}</div>
                {hint && <div style={{ fontSize: 12, color: colors.muted, marginTop: 2 }}>{hint}</div>}
              </div>
            </>
          )}
        </div>
      </div>
    );
  },
);

FileInput.displayName = 'FileInput';

export default FileInput;
